/**
 * <TagChips /> — read-only inline chip list.
 * <TagsInput /> — chip input with backspace-to-delete, comma/space/Enter to
 *                  commit, accent-stripped normalisation.
 *
 * Tags are stored lowercase/alphanumeric on the backend. The UI keeps them
 * readable as you type but normalises on commit so duplicates collapse.
 */
import { useState } from "react";
import { Tag as TagIcon, X } from "lucide-react";

const STRIP_ACCENTS = (s) =>
  s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");

export function normalizeTag(raw) {
  if (!raw) return "";
  const t = STRIP_ACCENTS(String(raw)).trim().toLowerCase();
  return t.replace(/[^a-z0-9_-]/g, "");
}

const PALETTE = [
  "bg-sky-50 text-sky-700 ring-sky-200",
  "bg-emerald-50 text-emerald-700 ring-emerald-200",
  "bg-amber-50 text-amber-700 ring-amber-200",
  "bg-rose-50 text-rose-700 ring-rose-200",
  "bg-violet-50 text-violet-700 ring-violet-200",
  "bg-indigo-50 text-indigo-700 ring-indigo-200",
  "bg-teal-50 text-teal-700 ring-teal-200",
  "bg-orange-50 text-orange-700 ring-orange-200",
];

function _hashTag(tag) {
  let h = 0;
  for (let i = 0; i < tag.length; i += 1) {
    h = (h * 31 + tag.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(h) % PALETTE.length];
}

export function TagChips({ tags, size = "xs", onRemove = null, testid = "tags" }) {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  const cls = size === "sm"
    ? "text-[11px] px-2 py-0.5"
    : "text-[10px] px-1.5 py-0.5";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      {tags.map((t) => (
        <span
          key={t}
          data-testid={`${testid}-chip-${t}`}
          className={`inline-flex items-center gap-1 rounded-full ring-1 font-medium uppercase tracking-wider ${cls} ${_hashTag(t)}`}
        >
          <TagIcon className="h-2.5 w-2.5 opacity-60" />
          {t}
          {onRemove && (
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              data-testid={`${testid}-remove-${t}`}
              onClick={() => onRemove(t)}
              className="ml-0.5 opacity-60 hover:opacity-100"
              aria-label={`remove ${t}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

export function TagsInput({ value = [], onChange, placeholder, suggestions = [], testid = "tags-input" }) {
  const [draft, setDraft] = useState("");

  const commit = (raw) => {
    const next = normalizeTag(raw);
    if (!next) return;
    if (value.includes(next)) {
      setDraft("");
      return;
    }
    onChange([...value, next]);
    setDraft("");
  };

  const remove = (tag) => onChange(value.filter((t) => t !== tag));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  // Filter suggestions to those not already selected and matching the draft.
  const filteredSugg = suggestions
    .map((s) => normalizeTag(s))
    .filter((s) => s && !value.includes(s) && (draft ? s.includes(normalizeTag(draft)) : true))
    .slice(0, 6);

  return (
    <div data-testid={testid} className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500">
        <TagChips tags={value} size="sm" onRemove={remove} testid={`${testid}-chips`} />
        <input /* design-system: ALLOW legacy-todo */
          data-testid={`${testid}-field`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => draft.trim() && commit(draft)}
          placeholder={value.length ? "" : (placeholder || "tag1, tag2…")}
          className="flex-1 min-w-[80px] bg-transparent border-0 focus:outline-none text-sm py-0.5"
        />
      </div>
      {filteredSugg.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${testid}-suggestions`}>
          {filteredSugg.map((s) => (
            <button /* design-system: ALLOW legacy-todo */
              key={s}
              type="button"
              data-testid={`${testid}-suggest-${s}`}
              onClick={() => commit(s)}
              className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 text-slate-600 transition-colors uppercase tracking-wider font-medium"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
